import type { WebApp } from '@twa-dev/types';
import { setSafeInsets } from '../app/viewport';

declare global {
  interface Window {
    Telegram?: { WebApp: WebApp };
  }
}

/** Bot API 8.0 safe-area shapes — read defensively so older `@twa-dev/types` still type-checks. */
interface SafeAreaInset {
  top: number;
  bottom: number;
  left: number;
  right: number;
}
interface InsetSource {
  safeAreaInset?: SafeAreaInset;
  contentSafeAreaInset?: SafeAreaInset;
}

type VerticalSwipeControl = {
  isVerticalSwipesEnabled?: boolean;
  disableVerticalSwipes?: () => void;
};

type OrientationLockControl = {
  isOrientationLocked?: boolean;
  lockOrientation?: () => void;
  unlockOrientation?: () => void;
};

export type KeyboardControl = {
  hideKeyboard?: () => void;
};

const touchGuardedCanvases = new WeakSet<HTMLCanvasElement>();

type HapticImpactStyle = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';
type HapticNotificationStyle = 'error' | 'success' | 'warning';
type TelegramFeedback = {
  impactOccurred(style: HapticImpactStyle): void;
  notificationOccurred(style: HapticNotificationStyle): void;
  selectionChanged(): void;
};

const HAPTICS_KEY = 'pardis:haptics';
let activeBackHandler: (() => void) | null = null;

export interface TelegramLifecycleHandlers {
  activated(): void;
  deactivated(): void;
}

export function isHapticsEnabled(): boolean {
  try {
    return localStorage.getItem(HAPTICS_KEY) !== '0';
  } catch {
    return true;
  }
}

export function setHapticsEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(HAPTICS_KEY, enabled ? '1' : '0');
  } catch {
    /* persistence is best-effort */
  }
}

function feedback(): TelegramFeedback | null {
  if (!isHapticsEnabled()) return null;
  return (window.Telegram?.WebApp as unknown as { HapticFeedback?: TelegramFeedback } | undefined)?.HapticFeedback ?? null;
}

export function hapticImpact(style: HapticImpactStyle = 'light'): void {
  try {
    feedback()?.impactOccurred(style);
  } catch {
    /* unavailable on older Telegram clients */
  }
}

export function hapticSelection(): void {
  try {
    feedback()?.selectionChanged();
  } catch {
    /* unavailable on older Telegram clients */
  }
}

export function hapticNotification(style: HapticNotificationStyle): void {
  try {
    feedback()?.notificationOccurred(style);
  } catch {
    /* unavailable on older Telegram clients */
  }
}

/** Hide Telegram's native software keyboard when supported (Bot API 9.1+); DOM blur is handled by the caller. */
export function hideTelegramKeyboard(
  control: KeyboardControl | null | undefined =
    typeof window === 'undefined' ? undefined : (window.Telegram?.WebApp as unknown as KeyboardControl | undefined),
): void {
  try {
    control?.hideKeyboard?.();
  } catch {
    /* older clients and incomplete SDK shims fall back to the DOM blur */
  }
}

/** Show Telegram's native header back control, or hide it at app roots/game tables. */
export function setTelegramBackButton(handler: (() => void) | null): void {
  const back = (window.Telegram?.WebApp as unknown as {
    BackButton?: { show(): void; hide(): void; onClick(cb: () => void): void; offClick(cb: () => void): void };
  } | undefined)?.BackButton;
  if (!back) return;
  try {
    if (activeBackHandler) back.offClick(activeBackHandler);
    activeBackHandler = handler;
    if (handler) {
      back.onClick(handler);
      back.show();
    } else {
      back.hide();
    }
  } catch {
    activeBackHandler = null;
  }
}

/** Subscribe to Telegram foreground/background events when the host version supports them. */
export function installTelegramLifecycleHandlers(handlers: TelegramLifecycleHandlers): () => void {
  const tg = window.Telegram?.WebApp as unknown as {
    onEvent?(event: string, callback: () => void): void;
    offEvent?(event: string, callback: () => void): void;
  } | undefined;
  if (!tg?.onEvent) return () => undefined;
  try {
    tg.onEvent('activated', handlers.activated);
    tg.onEvent('deactivated', handlers.deactivated);
  } catch {
    return () => undefined;
  }
  return () => {
    try {
      tg.offEvent?.('activated', handlers.activated);
      tg.offEvent?.('deactivated', handlers.deactivated);
    } catch {
      /* older clients may expose onEvent without offEvent */
    }
  };
}

/** Ask Telegram to confirm closing only while abandoning an active game would be surprising. */
export function setTelegramClosingConfirmation(enabled: boolean): void {
  const tg = window.Telegram?.WebApp as unknown as {
    enableClosingConfirmation?: () => void;
    disableClosingConfirmation?: () => void;
  } | undefined;
  try {
    if (enabled) tg?.enableClosingConfirmation?.();
    else tg?.disableClosingConfirmation?.();
  } catch {
    /* feature unavailable on older Telegram clients */
  }
}

/** Prevent content drags from being interpreted as Telegram's close/minimize gesture (Bot API 7.7+). */
export function disableTelegramVerticalSwipes(tg: VerticalSwipeControl): void {
  try {
    // Detect the capability itself: version strings and incomplete SDK wrappers are not reliable.
    if (typeof tg.disableVerticalSwipes !== 'function' || tg.isVerticalSwipesEnabled === false) return;
    tg.disableVerticalSwipes();
  } catch {
    /* older clients or incomplete SDK shims keep their default swipe behavior */
  }
}

/**
 * Lock only after Telegram reports a portrait viewport. The API locks the current mode rather than
 * accepting a desired orientation, so a landscape launch must stay unlocked until it turns portrait.
 * Calling lockOrientation even when the SDK's cached flag is true deliberately re-sends the native
 * event after a page reload.
 */
export function syncTelegramPortraitOrientation(tg: OrientationLockControl, isPortrait: boolean): void {
  try {
    if (isPortrait) {
      tg.lockOrientation?.();
    } else if (tg.isOrientationLocked === true) {
      tg.unlockOrientation?.();
    }
  } catch {
    /* older clients or incomplete SDK shims keep their default orientation behavior */
  }
}

/**
 * Stop iOS WebView scrolling only on the Pixi surface. preventDefault does not stop propagation, so
 * Pixi still receives the drag while Telegram cannot promote it to its close/minimize gesture.
 */
export function installIosCanvasTouchGuard(canvas: HTMLCanvasElement, enabled = isIosDevice()): void {
  if (!enabled || touchGuardedCanvases.has(canvas)) return;
  touchGuardedCanvases.add(canvas);
  canvas.style.touchAction = 'none';
  canvas.addEventListener('touchmove', (event) => event.preventDefault(), { passive: false });
}

/**
 * Push Telegram's safe-area insets into the layout. `safeAreaInset` is the device safe area (notch /
 * status bar / home indicator); `contentSafeAreaInset` is the *extra* space Telegram's own UI (the
 * top-right close/menu buttons) occupies INSIDE that safe area — so the two are additive, not
 * overlapping. Re-letterbox afterwards via the existing `resize` nudge.
 */
function applySafeInsets(tg: WebApp): void {
  const src = tg as unknown as InsetSource;
  const sa = src.safeAreaInset;
  const ca = src.contentSafeAreaInset;
  const top = (sa?.top ?? 0) + (ca?.top ?? 0);
  const bottom = (sa?.bottom ?? 0) + (ca?.bottom ?? 0);
  setSafeInsets(top, bottom);
  window.dispatchEvent(new Event('resize'));
}

/**
 * Initialize Telegram Mini App integration. No-op when not running inside Telegram; the boot layer
 * then shows the Telegram-only screen without opening a gateway connection. It tells Telegram the app is ready,
 * expands to full height, matches the app's dark chrome, and keeps the Pixi canvas sized to the
 * Telegram viewport.
 */
export function initTelegram(): void {
  const tg = window.Telegram?.WebApp;
  if (!tg) return;

  tg.ready();
  tg.expand();
  disableTelegramVerticalSwipes(tg);

  // Launch full-screen by default when supported (Bot API 8.0+); older clients keep the expanded view.
  try {
    if (tg.isVersionAtLeast('8.0') && !tg.isFullscreen) {
      tg.requestFullscreen();
    }
  } catch {
    /* requestFullscreen unsupported or rejected — the expanded view is fine */
  }

  // Keep the Telegram viewport portrait. Backgammon already rotates its own world by 90°, so letting
  // the host rotate as well only moves Telegram's native header across the top edge of the board.
  // Telegram can lock only the *current* mode: if launched in landscape, wait until the player turns
  // portrait instead of accidentally locking the unwanted landscape layout.
  const portraitQuery = window.matchMedia('(orientation: portrait)');
  let appliedPortraitMode: boolean | null = null;
  const syncPortraitLock = (): void => {
    const isPortrait = portraitQuery.matches;
    if (isPortrait === appliedPortraitMode) return;
    appliedPortraitMode = isPortrait;
    syncTelegramPortraitOrientation(tg as unknown as OrientationLockControl, isPortrait);
  };
  syncPortraitLock();
  if (typeof portraitQuery.addEventListener === 'function') {
    portraitQuery.addEventListener('change', syncPortraitLock);
  } else {
    // Safari before MediaQueryList.addEventListener; harmless on current Telegram/iOS.
    (portraitQuery as MediaQueryList & { addListener?(listener: () => void): void }).addListener?.(syncPortraitLock);
  }

  // Match the game's dark wood/green chrome (older clients may not support these).
  try {
    tg.setHeaderColor('#021b0e');
    tg.setBackgroundColor('#021b0e');
  } catch {
    /* ignore on clients that don't support theming */
  }

  // PixiApp uses `resizeTo: window`; nudging a resize re-letterboxes the canvas after Telegram
  // changes the viewport (expand / fullscreen / on-screen keyboard / rotation).
  const reflow = () => {
    disableTelegramVerticalSwipes(tg);
    window.dispatchEvent(new Event('resize'));
  };
  tg.onEvent('viewportChanged', reflow);
  tg.onEvent('fullscreenChanged', reflow);

  // Reserve the fullscreen safe areas so content clears the notch/camera + Telegram's top buttons and
  // the rounded bottom corner. Apply now (may be 0 until fullscreen resolves) and on every change.
  applySafeInsets(tg);
  const onSafe = tg as unknown as { onEvent(event: string, cb: () => void): void };
  onSafe.onEvent('safeAreaChanged', () => applySafeInsets(tg));
  onSafe.onEvent('contentSafeAreaChanged', () => applySafeInsets(tg));
  tg.onEvent('fullscreenChanged', () => applySafeInsets(tg));
}

/** True only for a genuine Telegram launch carrying raw initData. */
export function hasTelegramLaunchData(): boolean {
  const tg = window.Telegram?.WebApp;
  return Boolean(tg && typeof tg.initData === 'string' && tg.initData.length > 0);
}

/**
 * Whether this browser is running on iOS. Telegram's own platform value is authoritative when it is
 * available; the user-agent fallback keeps the gate working in older clients that report `unknown`.
 * Modern iPads identify themselves as MacIntel, so touch capability is included in that fallback.
 */
export function isIosPlatform(
  telegramPlatform: string | undefined,
  userAgent: string,
  browserPlatform = '',
  maxTouchPoints = 0,
): boolean {
  const platform = telegramPlatform?.trim().toLowerCase();
  if (platform && platform !== 'unknown') return platform === 'ios';

  return /iPad|iPhone|iPod/i.test(userAgent) ||
    (browserPlatform === 'MacIntel' && maxTouchPoints > 1);
}

/** True only on iPhone/iPad/iPod; used as a UI compatibility gate, not an authentication control. */
export function isIosDevice(): boolean {
  const tg = window.Telegram?.WebApp;
  return isIosPlatform(
    tg?.platform,
    window.navigator.userAgent,
    window.navigator.platform,
    window.navigator.maxTouchPoints,
  );
}

/** Keep local browser development cross-platform; every hosted build remains iOS-only. */
export function shouldEnforceIosOnly(hostname: string): boolean {
  const host = hostname.trim().toLowerCase();
  return host !== 'localhost' && host !== '127.0.0.1' && host !== '[::1]' && !host.endsWith('.localhost');
}

/** The unauthenticated two-client harness is available only from a Vite development build on loopback. */
export function isLocalDevelopment(hostname: string, isDevBuild: boolean): boolean {
  return isDevBuild && !shouldEnforceIosOnly(hostname);
}

/** Select a stable local gateway identity for each dev-multi iframe. */
export function localDevelopmentClientId(search: string): 'a' | 'b' {
  const client = new URLSearchParams(search).get('client')?.trim().toLowerCase();
  return client === 'b' ? 'b' : 'a';
}

/**
 * Exchange Telegram's raw, signed initData for the gateway's HttpOnly session cookie. The raw data
 * is never stored or logged by the client. A valid existing cookie lets the gateway return quickly.
 */
export async function authenticateTelegram(wsUrl: string): Promise<void> {
  const tg = window.Telegram?.WebApp;
  if (!tg || !hasTelegramLaunchData()) {
    throw new Error('telegram_launch_required');
  }

  const endpoint = new URL(wsUrl, window.location.href);
  endpoint.protocol = endpoint.protocol === 'wss:' ? 'https:' : 'http:';
  endpoint.pathname = '/auth/telegram';
  endpoint.search = '';
  endpoint.hash = '';

  const response = await fetch(endpoint, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ initData: tg.initData }),
  });
  if (!response.ok) {
    throw new Error(`telegram_auth_failed:${response.status}`);
  }
}

/**
 * Open an external URL the way Unity's `Application.OpenURL` does. Inside Telegram a `t.me`/`tg:` link
 * is handled by `openTelegramLink` (navigates within Telegram to the channel/bot); any other URL goes
 * through `openLink` (Telegram's in-app browser). In a plain browser it opens a new tab. Falls back to
 * `window.open` if the Telegram call throws on an older client.
 */
export function openExternalUrl(url: string): void {
  if (!url) return;
  const tg = window.Telegram?.WebApp;
  if (tg) {
    try {
      const isTelegramLink = /^(https?:\/\/(t\.me|telegram\.me|telegram\.dog)\/|tg:\/\/)/i.test(url);
      if (isTelegramLink) {
        (tg as unknown as { openTelegramLink(u: string): void }).openTelegramLink(url);
      } else {
        tg.openLink(url);
      }
      return;
    } catch {
      /* fall through to the browser fallback */
    }
  }
  window.open(url, '_blank', 'noopener');
}

/**
 * Exit the app the way Unity's "خروج" button does (`Application.Quit()`): leave WITHOUT forfeiting.
 * Inside Telegram this closes the Mini App — the server keeps the game alive (GameState.Running), so
 * reopening reconnects and resumes it. In a plain browser there is no app to quit, so this falls back
 * to `window.close()` (a no-op for tabs the script didn't open) and reports false to the caller.
 */
export function closeApp(): boolean {
  const tg = window.Telegram?.WebApp;
  if (tg) {
    try {
      tg.close();
      return true;
    } catch {
      /* fall through to the browser fallback */
    }
  }
  window.close();
  return false;
}
